import React, { MutableRefObject, ReactElement, useEffect, useMemo, useRef } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { useFormContext, ValidateResult } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { SettingsLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { ALL_VALUE } from '../../Common/API/ItemsGetters'
import { CollapseArea } from '../../Common/Components/CollapseArea/CollapseArea'
import FormRow from '../../Common/Components/FormRow'
import { ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import { ANY_VALUE_STRING_NUMERIC } from '../../Common/Constants/DefaultValues'
import { ReactState } from '../../Common/Contexts'
import { DEFAULT_CME_ID } from '../Constants'
import {
    AdministratorNameSpacing,
    AUTH_NOTIFICATIONS_COMPONENT_NAME,
    CME_PERM_NAME,
    CONCURRENT_SIGNINS_COMPONENT_NAME,
    DRAG_AND_DROP_COMPONENT_NAME,
    JAVASCRIPT_OUTPUT_COMPONENT_NAME,
    LOCKED_OUT_COMPONENT_NAME,
    MEDIA_PLAYER_COMPONENT_NAME,
    NEW_DEVICE_NOTIFICATIONS_COMPONENT_NAME,
    NOTIFY_PASSWORD_COMPONENT_NAME,
    PROMPT_TOS_COMPONENT_NAME,
    REPORTING_LEVEL_NAME,
    ROLE_PERMISSIONS,
    RolePermissionsFieldNames,
    SERVICE_NOTIF_COMPONENT_NAME,
    TEL_SYS_PERM_NAME,
    UPDATE_INTERVAL_COMPONENT_NAME
} from '../FieldNames'
import { FormPermissions } from '../Permissions/Permissions'
import { ReportingAccessLevels } from '../Reporting/ReportingAccessLevels'
import { InputHelp } from '../../Common/Components/InputHelp/InputHelp'
import { useFeatureFlag } from '../../Common/Hooks/useFeatureFlag'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import { OutlinedTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedTextInput'

const NON_DIGIT_REGEX: RegExp = /\D/g

type Props = {
    isAdmin?: boolean
    scope?: FormPermissions
    isRootScopeState: ReactState<boolean>
    viewOnly?: boolean
}

/**
 * Add User Settings content.
 *
 * @param isAdmin - Is this user an admin?
 * @param scope - Form permissions data from the user API.
 * @param concurrentShowState - Scope variable for whether to show the concurrent signins option.
 */
const Settings: React.FC<Props> = ({
    isAdmin,
    scope,
    isRootScopeState: [showConcurrent, setShowConcurrent],
    viewOnly
}: Props): ReactElement => {
    // Access language resources directly without context as this is top level component.
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle('SettingsLanguage')

    // Contexts
    const { watch, setValue } = useFormContext()

    const showAuthNotificationsFlag: Definable<boolean> = useFeatureFlag('showAuthNotifications')
    const showNewDeviceNotificationsFlag: Definable<boolean> = useFeatureFlag('showNewDeviceNotifications')

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const updateIntervalValue: string = watch(UPDATE_INTERVAL_COMPONENT_NAME)
    const cmeValue: string = watch(CME_PERM_NAME)
    const telSysValue: number = watch(TEL_SYS_PERM_NAME)
    const reportingLevelValue: string = watch(REPORTING_LEVEL_NAME)
    const rolePermissionsValue: Record<string, Record<string, boolean>> = watch(
        AdministratorNameSpacing(ROLE_PERMISSIONS)
    )
    const telSysPerms: Record<string, boolean> = rolePermissionsValue?.[RolePermissionsFieldNames.TELEPHONY_SERVER]
    const userPerms: Record<string, boolean> = rolePermissionsValue?.[RolePermissionsFieldNames.APPLICATION_USER]
    const devicePerms: Record<string, boolean> = rolePermissionsValue?.[RolePermissionsFieldNames.DEVICES]
    const eptAccessPerms: Record<string, boolean> = rolePermissionsValue?.[RolePermissionsFieldNames.EPT_ACCESS]

    const isEssentialsUser: boolean = useMemo(
        (): boolean => reportingLevelValue === ReportingAccessLevels.ESSENTIALS,
        [reportingLevelValue]
    )
    const isProfessionalOrAgentUser: boolean = useMemo(
        (): boolean =>
            reportingLevelValue === ReportingAccessLevels.PROFESSIONAL ||
            reportingLevelValue === ReportingAccessLevels.CALL_CENTRE_AGENT,
        [reportingLevelValue]
    )

    const showConcurrentSignins: boolean = useMemo((): boolean => {
        if (isEssentialsUser || isProfessionalOrAgentUser) {
            return false
        }

        return showConcurrent
    }, [isEssentialsUser, showConcurrent])

    const showAuthNotifications: boolean = useMemo(
        (): boolean =>
            !!showAuthNotificationsFlag &&
            telSysValue === ALL_VALUE &&
            !!telSysPerms?.Read &&
            !!telSysPerms?.Create &&
            !!telSysPerms?.Update,
        [telSysValue, telSysPerms, showAuthNotificationsFlag]
    )

    const showNewDeviceNotifications: boolean = useMemo(
        (): boolean =>
            !!showNewDeviceNotificationsFlag &&
            telSysValue === ALL_VALUE &&
            ((!!userPerms?.Create && !!userPerms?.Update && !!devicePerms?.Read) || eptAccessPerms?.Allowed),
        [telSysValue, telSysPerms, showNewDeviceNotificationsFlag]
    )

    useEffect((): void => {
        if (Number.isNaN(parseInt(updateIntervalValue, 10)) || updateIntervalValue === ANY_VALUE_STRING_NUMERIC) {
            setValue(UPDATE_INTERVAL_COMPONENT_NAME, language.get(SettingsLanguage.AutoValue))
        } else if (updateIntervalValue.match(NON_DIGIT_REGEX)) {
            setValue(UPDATE_INTERVAL_COMPONENT_NAME, updateIntervalValue.replace(NON_DIGIT_REGEX, ''))
        }
    }, [updateIntervalValue])

    useEffect((): void => {
        setTimeout((): void => {
            if (isMounted.current) {
                if (cmeValue && telSysValue) {
                    setShowConcurrent(cmeValue === DEFAULT_CME_ID && telSysValue === ALL_VALUE)
                } else {
                    setShowConcurrent((scope?.telephonyserver ?? ALL_VALUE) === ALL_VALUE)
                }
            }
        })
    }, [scope, telSysValue, cmeValue])

    const hasInitialised: MutableRefObject<boolean> = useRef(false)

    useEffect((): void => {
        if (!showConcurrent && hasInitialised.current) {
            setValue(CONCURRENT_SIGNINS_COMPONENT_NAME, false)
        }
    }, [showConcurrent])

    useEffect((): void => {
        hasInitialised.current = true
    }, [])

    return (
        <div className='settings-tab tab-area'>
            <FormRow className='locked-out'>
                <RouterCheckbox
                    labelLeft={language.get(SettingsLanguage.LockedOut)}
                    name={LOCKED_OUT_COMPONENT_NAME}
                    disabled={viewOnly}
                />
            </FormRow>
            <FormRow className='prompt-terms-of-service'>
                <RouterCheckbox
                    labelLeft={language.get(SettingsLanguage.PromptTos)}
                    name={PROMPT_TOS_COMPONENT_NAME}
                    disabled={viewOnly}
                />
            </FormRow>
            <FormRow className='notify-password-change'>
                <RouterCheckbox
                    labelLeft={language.get(SettingsLanguage.NotifyPasswordChange)}
                    name={NOTIFY_PASSWORD_COMPONENT_NAME}
                    disabled={viewOnly}
                />
            </FormRow>
            {showConcurrentSignins && (
                <FormRow className='concurrent-sign-in'>
                    <RouterCheckbox
                        disabled={viewOnly}
                        labelLeft={language.get(SettingsLanguage.SettingsLanguage_ConcurrentSignins)}
                        name={CONCURRENT_SIGNINS_COMPONENT_NAME}
                    />
                </FormRow>
            )}
            {isAdmin && (
                <FormRow className='service-notifications'>
                    <RouterCheckbox
                        disabled={!isAdmin || viewOnly}
                        labelLeft={language.get(SettingsLanguage.SettingsLanguage_ServiceNotification)}
                        name={SERVICE_NOTIF_COMPONENT_NAME}
                    />
                </FormRow>
            )}
            {showAuthNotifications ? (
                <FormRow className='auth-notifications'>
                    <InputHelp text={language.get(SettingsLanguage.SettingsLanguage_AuthNotifications_Help)}>
                        <RouterCheckbox
                            disabled={!isAdmin || viewOnly}
                            labelLeft={language.get(SettingsLanguage.SettingsLanguage_AuthNotifications)}
                            name={AUTH_NOTIFICATIONS_COMPONENT_NAME}
                        />
                    </InputHelp>
                </FormRow>
            ) : (
                <></>
            )}
            {showNewDeviceNotifications ? (
                <FormRow className='device-notifications'>
                    <InputHelp text={language.get(SettingsLanguage.SettingsLanguage_NewDeviceNotifications_Help)}>
                        <RouterCheckbox
                            disabled={!isAdmin || viewOnly}
                            labelLeft={language.get(SettingsLanguage.SettingsLanguage_NewDeviceNotifications)}
                            name={NEW_DEVICE_NOTIFICATIONS_COMPONENT_NAME}
                        />
                    </InputHelp>
                </FormRow>
            ) : (
                <></>
            )}
            <CollapseArea
                className='highlight-fields'
                title={language.get(SettingsLanguage.SettingsLanguage_AdvancedTitle)}
            >
                <FormRow className='update-interval'>
                    <ValidationErrors name={UPDATE_INTERVAL_COMPONENT_NAME}>
                        <OutlinedTextInput
                            className='update-interval'
                            labelLeft={language.get(SettingsLanguage.UpdateInterval)}
                            inputName={UPDATE_INTERVAL_COMPONENT_NAME}
                            rules={{
                                validate: {
                                    maxInt: (value: number): ValidateResult => {
                                        if (value > 180) {
                                            return language.get(SettingsLanguage.UpdateIntervalValidation)
                                        }

                                        return undefined
                                    }
                                }
                            }}
                            adornmentEnd={
                                !isNaN(parseInt(updateIntervalValue, 10))
                                    ? language.get(SettingsLanguage.UpdateIntervalCharacter)
                                    : undefined
                            }
                            onClick={({ currentTarget }: React.MouseEvent<Element, MouseEvent>): void => {
                                currentTarget?.querySelector('input')?.select()
                            }}
                            maxLength={6}
                            disabled={viewOnly}
                        />
                    </ValidationErrors>
                </FormRow>
                <FormRow className='media-player'>
                    <RouterCheckbox
                        labelLeft={language.get(SettingsLanguage.MediaPlayer)}
                        name={MEDIA_PLAYER_COMPONENT_NAME}
                        disabled={viewOnly}
                    />
                </FormRow>
                <FormRow className='drag-and-drop'>
                    <RouterCheckbox
                        labelLeft={language.get(SettingsLanguage.DragAndDrop)}
                        name={DRAG_AND_DROP_COMPONENT_NAME}
                        disabled={viewOnly}
                    />
                </FormRow>
                <FormRow className='javascript-output'>
                    <RouterCheckbox
                        labelLeft={language.get(SettingsLanguage.JavascriptOutput)}
                        name={JAVASCRIPT_OUTPUT_COMPONENT_NAME}
                        disabled={viewOnly}
                    />
                </FormRow>
            </CollapseArea>
        </div>
    )
}

export default Settings
